import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { notificationAPI } from "../services/api";

export default function Notifications() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const data = await notificationAPI.getAll();
      setNotifications(data);
    } catch (err) {
      setError("Failed to load notifications");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await notificationAPI.markAsRead(id);
      // Update local state
      setNotifications(notifications.map(n => 
        n.id === id ? { ...n, isRead: true, readAt: new Date().toISOString() } : n
      ));
    } catch (err) {
      console.error("Failed to mark as read:", err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationAPI.markAllAsRead();
      // Update all notifications to read
      setNotifications(notifications.map(n => ({ ...n, isRead: true, readAt: new Date().toISOString() })));
      alert("All notifications marked as read");
    } catch (err) {
      alert("Failed to mark all as read");
      console.error(err);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'success': return '✅';
      case 'info': return 'ℹ️';
      case 'warning': return '⚠️';
      case 'error': return '❌';
      default: return '📬';
    }
  };

  const getNotificationColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'success': return '#28a745';
      case 'info': return '#007bff';
      case 'warning': return '#ffc107';
      case 'error': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;

  if (loading) {
    return (
      <div style={styles.loading}>
        <h2>Loading notifications...</h2>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1>🔔 Notifications</h1>
          {unreadCount > 0 && (
            <span style={styles.badge}>{unreadCount} unread</span>
          )}
        </div>
        <div style={styles.headerButtons}>
          {unreadCount > 0 && (
            <button onClick={markAllAsRead} style={styles.markAllButton}>
              Mark All as Read
            </button>
          )}
          <button onClick={() => navigate("/dashboard")} style={styles.backButton}>
            ← Back
          </button>
        </div>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {notifications.length === 0 ? (
        <div style={styles.empty}>
          <h2>📭 No Notifications</h2>
          <p>You don't have any notifications yet.</p>
        </div>
      ) : (
        <div style={styles.notificationList}>
          {notifications.map((notification) => (
            <div
              key={notification.id}
              style={{
                ...styles.notificationItem,
                backgroundColor: notification.isRead ? '#f8f9fa' : '#ffffff',
                borderLeft: `4px solid ${getNotificationColor(notification.type)}`,
              }}
              onClick={() => !notification.isRead && markAsRead(notification.id)}
            >
              <div style={styles.notificationIcon}>
                {getNotificationIcon(notification.type)}
              </div>
              <div style={styles.notificationContent}>
                <div style={styles.notificationHeader}>
                  <h3 style={styles.notificationTitle}>{notification.title}</h3>
                  {!notification.isRead && <span style={styles.unreadDot}>●</span>}
                </div>
                <p style={styles.notificationMessage}>{notification.message}</p>
                <small style={styles.notificationDate}>
                  {new Date(notification.createdAt).toLocaleString()}
                  {notification.readAt && ` • Read: ${new Date(notification.readAt).toLocaleString()}`}
                </small>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: "900px",
    margin: "0 auto",
    padding: "20px",
  },
  loading: {
    textAlign: "center",
    marginTop: "100px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "30px",
  },
  badge: {
    display: "inline-block",
    marginLeft: "10px",
    padding: "4px 12px",
    backgroundColor: "#dc3545",
    color: "white",
    borderRadius: "12px",
    fontSize: "14px",
    fontWeight: "500",
  },
  headerButtons: {
    display: "flex",
    gap: "10px",
  },
  markAllButton: {
    padding: "10px 20px",
    fontSize: "14px",
    fontWeight: "600",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    backgroundColor: "#007bff",
    color: "white",
  },
  backButton: {
    padding: "10px 20px",
    fontSize: "14px",
    fontWeight: "600",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    backgroundColor: "#6c757d",
    color: "white",
  },
  error: {
    backgroundColor: "#fee",
    color: "#c33",
    padding: "15px",
    borderRadius: "6px",
    marginBottom: "20px",
    border: "1px solid #fcc",
  },
  empty: {
    textAlign: "center",
    padding: "60px 20px",
    backgroundColor: "white",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
  notificationList: {
    display: "flex",
    flexDirection: "column",
    gap: "15px",
  },
  notificationItem: {
    display: "flex",
    padding: "20px",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    cursor: "pointer",
    transition: "all 0.3s",
  },
  notificationIcon: {
    fontSize: "32px",
    marginRight: "15px",
    flexShrink: 0,
  },
  notificationContent: {
    flex: 1,
  },
  notificationHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "8px",
  },
  notificationTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#333",
    margin: 0,
  },
  unreadDot: {
    color: "#007bff",
    fontSize: "20px",
  },
  notificationMessage: {
    fontSize: "14px",
    color: "#666",
    lineHeight: "1.5",
    margin: "0 0 8px 0",
  },
  notificationDate: {
    fontSize: "12px",
    color: "#999",
  },
};

